import type {IBreadcrumbItem} from 'components/FluentUICompat';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {DocumentStatus, Origin} from 'types';
import {GetRegulatoryDocumentDetailsQuery} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentDetails.generated';
import {DocOfRegDocDetailsPageQuery} from 'features/RegulatoryDocuments/RegDocDetailsPage/RegDocDetailsPage.queryTypes';
import {RegDocInjectedDataContext} from 'features/RegulatoryDocuments/RegDocDetailsPage/RegDocInjectedDataContext';
import {RegulatoryDocumentDetailsPage} from 'features/RegulatoryDocuments';
import {useGetexRegulationDetailsQuery} from './hooks/GetexRegulationDetails.generated';
import type {GetexRegulationDetailsQuery} from './hooks/GetexRegulationDetails.generated';

type GetexEntity = NonNullable<GetexRegulationDetailsQuery['getexRegulation']>;

/**
 * Maps a GetexRegulation entity to the shape expected by RegDocDetailsPage.
 * Fields that have no equivalent in GetexRegulation are set to null / empty defaults.
 */
const mapGetexToRegDoc = (entity: GetexEntity): DocOfRegDocDetailsPageQuery => {
	// GetexRegulation.keywords is string[] – wrap each value in a minimal Keyword object
	const keywords: DocOfRegDocDetailsPageQuery['keywords'] = (
		entity.keywords ?? []
	).map((name, i) => ({
		__typename: 'Keyword' as const,
		id: `getex-keyword-${i}`,
		name,
		vkos: [],
		vexClusters: [],
	}));

	return {
		__typename: 'RegulatoryDocument' as const,
		id: entity.id,
		name: entity.regulationNumber ?? '',
		summary: entity.regulationTitle,
		status: entity.status,
		disassemblyStatus: entity.disassemblyStatus,
		// Paragraphs exist, so treat as fully split
		documentStatus: DocumentStatus.NotSplit,
		modelYear: null,
		dateNewRegistration: null,
		dateNewTypes: null,
		dateEffective: entity.dateEffective ?? null,
		dateExpiration: null,
		origin: null as unknown as Origin,
		pogisExists: false,
		clearingAuditLogs: [],
		modifiedBy: entity.modifiedBy,
		createdBy: entity.createdBy,
		createdAt: entity.createdAt ?? null,
		modifiedAt: entity.modifiedAt ?? null,
		finalVersionRef: null,
		keywords,
		regulation: null,
		childrenRefs: null,
		workflow: entity.workflow,
		paragraphs: entity.paragraphs as DocOfRegDocDetailsPageQuery['paragraphs'],
		comments: null,
		faqs: null,
		phaseIn: [],
		phaseOut: [],
		predecessors: null,
		pogisDocuments: [],
		documentReferences: null,
		documentSource: null,
		topicManagementSummary: null,
		predecessor: null,
		successor: null,
		document: null,
		attachments: entity.attachments ?? null,
	};
};

const GetexAsRegDocDetailsPage: React.FC = () => {
	const {regulatoryDocumentId = ''} = useParams<{
		regulatoryDocumentId: string;
	}>();
	const {t} = useTranslation('features/getexregulation', {
		keyPrefix: 'GetexDetailsPage',
	});

	const {loading, data, networkStatus} = useGetexRegulationDetailsQuery({
		variables: {id: regulatoryDocumentId},
		notifyOnNetworkStatusChange: true,
	});

	const entity = data?.getexRegulation;

	const mappedData: GetRegulatoryDocumentDetailsQuery | undefined = entity
		? {regulatoryDocument: mapGetexToRegDoc(entity)}
		: undefined;

	const breadcrumbItems: IBreadcrumbItem[] = [
		{
			text: t('GetexRegulations'),
			key: 'getexRegulations',
			href: '/getexRegulations',
		},
		{
			text: entity?.regulationNumber ?? '',
			key: 'regulationNumber',
		},
	];

	const contextValue = useMemo(
		() => ({
			data: mappedData,
			isLoading: loading,
			networkStatus,
			breadcrumbItems,
			getexExtras: entity
				? {
						regulationNumber: entity.regulationNumber ?? '',
						regulationStatus: entity.regulationStatus ?? '',
						vehicleCategories: entity.vehicleCategories ?? [],
						traceKeywords: entity.traceKeywords ?? [],
				  }
				: undefined,
		}),
		[mappedData, loading, networkStatus, breadcrumbItems, entity],
	);

	return (
		<RegDocInjectedDataContext.Provider value={contextValue}>
			<RegulatoryDocumentDetailsPage />
		</RegDocInjectedDataContext.Provider>
	);
};

export default GetexAsRegDocDetailsPage;
